import { useEffect, useRef } from "react";
import { Purchases, ReservedCustomerAttribute } from "@revenuecat/purchases-js";
import { useSetAtom } from "jotai";
import useAuth from "./useAuth";
import { revenueCatReadyAtom } from "../jotai/atoms/subscription";
import { RC_ENTITLEMENT_ID } from "../config/revenuecat";
import { queryClient } from "../react-query/query-client";
import { QUERY_KEYS } from "../react-query/constants";
import { TTL } from "../utils/constants";
import Logger from "../utils/logger";

/**
 * To initialize RevenueCat Purchases SDK on app load, tied to user authentication state.
 */
export const useRevenueCatInit = () => {
  const { isLoggedIn, userProfile } = useAuth();
  const setReady = useSetAtom(revenueCatReadyAtom);
  const purchasesRef = useRef<Purchases | null>(null);

  useEffect(() => {
    if (!isLoggedIn || !userProfile?.id || Purchases.isConfigured()) {
      return;
    }

    const apiKey = process.env.REACT_APP_REVENUECAT_API_KEY;

    if (!apiKey) {
      Logger.error(
        "[RevenueCat] Missing REACT_APP_REVENUECAT_API_KEY environment variable"
      );
      return;
    }

    try {
      const purchases = Purchases.configure({
        apiKey,
        appUserId: userProfile.id,
      });

      purchases.setAttributes({
        [ReservedCustomerAttribute.Email]: userProfile.email,
        [ReservedCustomerAttribute.DisplayName]: [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' '),
        username: userProfile.username,
      });

      purchasesRef.current = purchases;
      setReady(true);

      queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.SUBSCRIPTION.IS_SUBSCRIBED],
        queryFn: async () => {
          const info = await purchases.getCustomerInfo();
          return info.entitlements.active?.[RC_ENTITLEMENT_ID];
        },
        staleTime: TTL.MINUTES(5),
      });

      queryClient.prefetchQuery({
        queryKey: [QUERY_KEYS.SUBSCRIPTION.OFFERINGS],
        queryFn: () => purchases.getOfferings(),
        staleTime: TTL.HOURS(1),
      });
    } catch (error) {
      Logger.error(`[RevenueCat] Initialization failed: ${error}`);
    }
  }, [
    isLoggedIn,
    userProfile?.id,
    setReady,
    userProfile?.email,
    userProfile?.username,
    userProfile?.firstName,
    userProfile?.lastName,
  ]);
};
